/**
 * Pure billing-display logic — the JSX-free half so credit/date formatting is
 * unit-tested without a DOM. Every NUMBER (balance, tier, plan, statement line)
 * comes from the `BillingSummaryView` the api returns; this only converts
 * micros → a display credit count and orders/labels rows. No domain logic: it
 * never decides a tier, a balance, or a quota (CLAUDE.md: cards/modals are
 * display-only, api is authority).
 */
import { pricingTier, type Tier } from '@4f/shared';
import type { LedgerEntryView, PlanView, SubscriptionView } from '../../api/types.ts';

/** 1 credit = 1e6 micros (billing.md § "credits are the unit"). */
const MICROS_PER_CREDIT = 1_000_000;

/** Convert a signed micros amount to a signed, whole-number credit count. */
export function microsToCredits(micros: number): number {
  return Math.round(micros / MICROS_PER_CREDIT);
}

/** The plan ladder in ascending tier order — `free` first, `max` last. */
const TIER_ORDER: readonly string[] = ['free', 'starter', 'plus', 'pro', 'max'];

/** Sort the plan catalog into the canonical ladder order (the api's order is a `Record`, unordered). */
export function sortPlans(plans: readonly PlanView[]): readonly PlanView[] {
  return [...plans].sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));
}

/**
 * A signed credit delta, formatted with an explicit `+`/`-` for a statement
 * line. The sign comes from the raw micros, not from `credits`: a sub-credit
 * debit rounds to `-0`, and `-0 >= 0` is `true`, so keying off `credits` would
 * mislabel a debit as `+0`.
 */
export function formatDelta(deltaMicros: number): string {
  const credits = microsToCredits(deltaMicros);
  return deltaMicros >= 0 ? `+${credits}` : `-${Math.abs(credits)}`;
}

/** Group consecutive-in-time statement lines is unnecessary at MVP scale — pass-through, newest first. */
export function recentLedger(entries: readonly LedgerEntryView[]): readonly LedgerEntryView[] {
  return entries;
}

/** A top-up pack's USD price, fixed to cents for display (the `$` is i18n copy). */
export function formatUsd(priceUsd: number): string {
  return priceUsd.toFixed(2);
}

/**
 * A plan's monthly USD price for display. `BillingSummaryView.plans` carries only
 * entitlements (credits) — the human-facing price lives in the ONE shared public
 * catalog (`@4f/shared` `PRICING_TIERS`), so a page never hardcodes a price and
 * marketing/app/API always quote the same ladder.
 */
export function planPriceUsd(tier: Tier): number {
  return pricingTier(tier).priceUsdMonthly;
}

/**
 * True when a plan row is something the user can actually BUY right now: it costs
 * money (the `free` tier is not a rail product — the edge rejects it with a `400`)
 * and it is not already their current tier. Keeping this pure means the "is there
 * anything to tap?" decision is unit-tested, not buried in JSX.
 */
export function isPurchasablePlan(plan: PlanView): boolean {
  return !plan.current && planPriceUsd(plan.tier) > 0;
}

/**
 * True when a cancel affordance should be offered — there IS a subscription and
 * it is not already canceled. Consumer-protection rule: anything buyable in-app
 * must be cancellable in-app, and the offer must reflect the server's status
 * rather than a client guess.
 */
export function canCancelSubscription(subscription: SubscriptionView | null): boolean {
  return subscription !== null && subscription.status !== 'canceled';
}
